import type { SceneAssetManifest } from "../lib/3d/sceneTypes";

export type GameEntry = {
  slug: string;
  title: string;
  strapline: string;
  description: string;
  features: string[];
  metrics: { label: string; value: string }[];
  palette: {
    primary: string;
    secondary: string;
  };
  sceneManifest: SceneAssetManifest;
};

export type HomeHeroWaypoint = {
  id: string;
  label: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaHref: string;
  scenePositionKey: string;
};

export type HomeSectionId = "studio" | "projects" | "team" | "about" | "contact";

export type HeroStageRenderer = "image" | "r3f";

export type HomeHeroMedia = {
  renderer: HeroStageRenderer;
  src: string;
  modelSrc?: string;
  alt: string;
  focalPoint: {
    x: number;
    y: number;
  };
  maxRotateX: number;
  maxRotateY: number;
  maxTranslateX: number;
  maxTranslateY: number;
  restingRotateX: number;
  restingRotateY: number;
};

export type HomeHeroTheme = {
  accent: string;
  accentSoft: string;
  mist: string;
  stoneLight: string;
  stoneMid: string;
  stoneDark: string;
  ink: string;
};

export type HeroFocusState = {
  hoveredId: string | null;
  lockedId: string | null;
  activeId: string | null;
};

export type StudioTeamMember = {
  name: string;
  role: string;
  imageSrc: string | null;
  body: string;
};

export const siteIcon = "/images/logo/logo color_circle.png";

export const homeSectionNavigation: { id: HomeSectionId; label: string; href: `/#${HomeSectionId}` }[] = [
  { id: "studio", label: "Studio", href: "/#studio" },
  { id: "projects", label: "Projects", href: "/#projects" },
  { id: "team", label: "Team", href: "/#team" },
  { id: "about", label: "About Us", href: "/#about" },
  { id: "contact", label: "Contact", href: "/#contact" }
];

export const teamSectionOverview = {
  eyebrow: "Team",
  word: "Studio",
  title: "The people building SevenStones.",
  intro:
    "SevenStones Studios brings together creative direction, gameplay engineering, and digital presentation to build culturally grounded games and immersive interactive experiences.",
  ctaLabel: "Talk to the studio",
  ctaHref: "/#contact"
};

export const teamSceneImageFallback = "/images/home/portfolio_ image concepts_2.png";

export const aboutSectionOverview = {
  eyebrow: "About the studio",
  title: "Culturally grounded games and immersive digital experiences.",
  intro:
    "SevenStones Studios creates narrative-led video games and interactive digital experiences shaped by culture, visual identity, and purposeful design.",
  ctaLabel: "Start a conversation",
  ctaHref: "/#contact"
};

export const aboutPhilosophy = [
  {
    title: "Culture with intention",
    body: "We build projects that draw strength from cultural roots, using story, symbolism, and identity as part of the experience rather than decoration."
  },
  {
    title: "Gameplay with meaning",
    body: "Mechanics, progression, and interaction should carry emotional weight and support the world they belong to."
  },
  {
    title: "Narrative and systems together",
    body: "Story, gameplay logic, and presentation are developed as one connected system so the work feels cohesive from first impression to final interaction."
  },
  {
    title: "Visual impact with discipline",
    body: "We care about atmosphere and cinematic presence, but always with clarity, usability, and long-term maintainability in mind."
  }
];
export const aboutImage =
{
  imageSrc: "/images/home/about-us-image.png"
};

export const aboutWhatWeDo = [
  {
    title: "Narrative-driven game development",
    body: "We create game concepts and playable experiences built around story, mood, and memorable worldbuilding."
  },
  {
    title: "Gameplay and multiplayer systems",
    body: "We develop gameplay architecture, multiplayer features, progression systems, and core interaction loops that hold up in production."
  },
  {
    title: "Immersive digital experiences",
    body: "We design digital surfaces and interactive presentations that extend the identity of a game or studio beyond the game itself."
  },
  {
    title: "Cinematic interactive presentation",
    body: "We shape interfaces, scenes, and visual pacing so the work feels polished, intentional, and emotionally resonant."
  },
  {
    title: "Cross-disciplinary production support",
    body: "We work across design, engineering, content, and production to turn ideas into shippable, readable, and compelling experiences."
  }
];

export const aboutProcess = [
  {
    step: "01",
    title: "Discover",
    body: "Define the story, cultural grounding, audience, and gameplay goals before expanding the visual or technical scope."
  },
  {
    step: "02",
    title: "Shape",
    body: "Develop the structure of the experience through world tone, interaction logic, and content direction so the core identity is clear early."
  },
  {
    step: "03",
    title: "Build",
    body: "Turn the approved direction into working systems, scenes, and content with a focus on quality, readability, and performance."
  },
  {
    step: "04",
    title: "Refine",
    body: "Polish the gameplay, presentation, and user flow until the final result feels deliberate, stable, and ready to represent the studio."
  }
];

export const aboutDifferences = [
  {
    title: "Beyond generic game branding",
    body: "Our work is not built around interchangeable trends. Each project is shaped by its own narrative voice, cultural context, and play style."
  },
  {
    title: "Beyond visual polish alone",
    body: "We connect art direction with gameplay systems, interaction design, and production thinking so the result works from the inside out."
  },
  {
    title: "Beyond one-time presentation",
    body: "We aim for work that can grow with the project, support future updates, and stay meaningful after the first reveal."
  }
];

export const aboutTeam: StudioTeamMember[] = [
  {
    name: "Nagu Shrestha",
    role: "Director",
    imageSrc: "/images/characters/che png.png",
    body: "Leads studio direction, creative vision, and the narrative identity behind SevenStones projects and public-facing experiences."
  },
  {
    name: "Shashi Shrestha",
    role: "Multiplayer & Gameplay Systems",
    imageSrc: "/images/characters/mala png.png",
    body: "Builds gameplay and multiplayer systems in Unity, including session flow, synchronization, core mechanics, and technical problem-solving across production."
  },
  {
    name: "Chiran Balami",
    role: "Content systems",
    imageSrc: "/images/characters/makara png.png",
    body: "Supports public-facing structure, studio language, and readable content systems that help SevenStones present its work clearly."
  }
];

export const aboutProof = [
  {
    title: "Culturally grounded concepts",
    body: "The studio’s projects are shaped by mythology, symbolism, humor, and local identity instead of anonymous genre dressing."
  },
  {
    title: "Gameplay systems capability",
    body: "From racing progression to multiplayer logic and interactive mechanics, the work is backed by real gameplay and engineering depth."
  },
  {
    title: "Narrative-led presentation",
    body: "Story, atmosphere, and visual impact are treated as essential parts of the experience across both games and digital surfaces."
  },
  {
    title: "Production-minded execution",
    body: "The work balances creativity with structure so ideas can move from concept into stable, shippable outcomes."
  }
];

export const aboutCollaborators = [
  "Game design",
  "Art direction",
  "Engineering",
  "Production"
];

export const aboutClosingCta = {
  title: "Let’s build the next world players step into.",
  body: "SevenStones creates games and immersive digital experiences where story, culture, gameplay, and visual identity work together as one system."
};

export const contactSectionOverview = {
  eyebrow: "Let’s connect",
  title: "Let’s create something extraordinary.",
  intro: "Have a project in mind or just want to say hello? We’d love to hear from you."
};

export const contactLocation = {
  label: "Based in Kathmandu, Nepal",
};

export const contactSocialLinks = [
  {
    href: "https://www.linkedin.com/",
    label: "LinkedIn",
    external: true as const
  },
  {
    href: "https://www.behance.net/",
    label: "Behance",
    external: true as const
  },
  {
    href: "https://www.instagram.com/",
    label: "Instagram",
    external: true as const
  }
];

export const contactInfoRows = [
  {
    icon: "mail",
    label: "Email us",
    value: "sevenstonesgames@gmail.com",
    href: "mailto:sevenstonesgames@gmail.com"
  },
  {
    icon: "chat",
    label: "Start a project",
    value: "sevenstonesgames@gmail.com",
    href: "mailto:sevenstonesgames@gmail.com"
  },
  {
    icon: "pin",
    label: "Visit us",
    value: "SevenStones Studio, Kathmandu, Nepal"
  }
];

export const contactLinks = [
  {
    href: "mailto:hello@sevenstones.studio",
    label: "hello@sevenstones.studio",
    external: true as const
  },
  {
    href: "https://drive.google.com",
    label: "Project Showcase",
    external: true as const
  }
];

export const studioStats = [
  { label: "Studio base", value: "Kathmandu, Nepal" },
  { label: "Company focus", value: "Games + immersive digital experiences" },
  { label: "Creative lens", value: "Narrative + culture + visual impact" }
];

export const homeHeroWaypoints: HomeHeroWaypoint[] = [
  {
    id: "worlds",
    label: "Worlds",
    title: "Build worlds rooted in story, culture, and visual identity.",
    description:
      "Lead with atmosphere and meaning so the visitor feels the character of SevenStones before they start reading individual features.",
    ctaLabel: "See the studio approach",
    ctaHref: "/about",
    scenePositionKey: "summit"
  },
  {
    id: "games",
    label: "Games",
    title: "Each project carries its own tone, mechanics, and mythology.",
    description:
      "From cultural racing to symbolic platforming and playful narrative concepts, every game route should reflect a distinct identity.",
    ctaLabel: "Open featured games",
    ctaHref: "/games/mystic-tempo",
    scenePositionKey: "upper"
  },
  {
    id: "web",
    label: "Experience",
    title: "Interactive presentation should support the game, not distract from it.",
    description:
      "Keep the site readable, expressive, and emotionally aligned with the studio so the public-facing experience strengthens the work it represents.",
    ctaLabel: "Review the foundation",
    ctaHref: "/about",
    scenePositionKey: "middle"
  },
  {
    id: "pipeline",
    label: "Pipeline",
    title: "Story, gameplay systems, and presentation are built with the same discipline.",
    description:
      "The strongest results come when direction, engineering, and content structure are aligned from the first pass instead of stitched together late.",
    ctaLabel: "See how we work",
    ctaHref: "/about",
    scenePositionKey: "lower"
  },
  {
    id: "contact",
    label: "Contact",
    title: "Turn attention into a clear next step.",
    description:
      "The homepage should guide visitors from curiosity into conversation, giving SevenStones a clear, confident way to present its next project.",
    ctaLabel: "Start a project",
    ctaHref: "/contact",
    scenePositionKey: "base"
  }
];

export const homeHeroOverview = {
  label: "Games Rooted in Culture",
  title: "Culturally Grounded Video Games",
  description:
    "We create world-class games Inspired by heritage, history, and the stories that connect us."
};

export const homeHeroMedia: HomeHeroMedia = {
  renderer: "r3f",
  src: "/images/home/home page_all-notext-mtadded.png",
  modelSrc: "/models/home/stone-stack-slanted-stairs.glb",
  alt: "Hero artwork for SevenStones Studios representing its multimedia and video game focus.",
  focalPoint: {
    x: 100,
    y: 50
  },
  maxRotateX: 28,
  maxRotateY: 48,
  maxTranslateX: 28,
  maxTranslateY: 32,
  restingRotateX: 4,
  restingRotateY: -12
};

export const contactImplementationOrder = [
  "Lead with the studio story and cultural point of view.",
  "Present featured games with distinct identities and clear project routes.",
  "Keep the homepage readable and conversion-friendly across devices.",
  "Expand richer interactive presentation only where it strengthens the studio message."
];

export const games: GameEntry[] = [
  {
    slug: "mystic-tempo",
    title: "Mystic Tempo Race",
    strapline: "A cultural racing game rooted in Nepalese mythology and story-driven challenge.",
    description:
      "Mystic Tempo Race combines racing, myth-inspired worldbuilding, and story-mode systems into a fast-moving experience shaped by Nepalese cultural references.",
    features: ["Story-driven race progression", "Myth-inspired worldbuilding", "Gameplay and multiplayer systems"],
    metrics: [
      { label: "Genre", value: "Cultural racing" },
      { label: "Core focus", value: "Story-driven gameplay" },
      { label: "World lens", value: "Nepalese mythology" }
    ],
    palette: {
      primary: "#5dd0ff",
      secondary: "#ff8f59"
    },
    sceneManifest: {
      id: "mystic-tempo-showcase",
      preloadPriority: "high",
      assets: ["cdn://games/mystic-tempo/poster.webp", "cdn://games/mystic-tempo/hero.glb"]
    }
  },
  {
    slug: "marapra",
    title: "MARAPRA (Makara-Prakriti)",
    strapline: "A meditative 2D action-adventure platformer exploring balance, shadow, and spirit.",
    description:
      "MARAPRA is a symbolic platforming experience built around reflective pacing, artful mechanics, and a world shaped by contrast, ritual, and inner movement.",
    features: ["Meditative action-platforming", "Symbolic worldbuilding", "Art-forward gameplay identity"],
    metrics: [
      { label: "Genre", value: "2D action-adventure" },
      { label: "Core focus", value: "Balance and spirit" },
      { label: "Tone", value: "Meditative" }
    ],
    palette: {
      primary: "#f6a54d",
      secondary: "#ff5e7a"
    },
    sceneManifest: {
      id: "marapra-showcase",
      preloadPriority: "medium",
      assets: ["cdn://games/marapra/poster.webp", "cdn://games/marapra/relic.glb"]
    }
  },
  {
    slug: "momo-mania",
    title: "Mo:mo Mania",
    strapline: "A humorous yet heartfelt 2D puzzle-platformer about recovering Nepal’s eternal momo recipe.",
    description:
      "Mo:mo Mania brings playful energy, cultural humor, and lighthearted platforming into a character-driven adventure built around food, charm, and momentum.",
    features: ["Puzzle-platforming structure", "Humor with heart", "Culturally specific concept"],
    metrics: [
      { label: "Genre", value: "2D puzzle-platformer" },
      { label: "Core focus", value: "Character and comedy" },
      { label: "Tone", value: "Playful" }
    ],
    palette: {
      primary: "#8ccf7e",
      secondary: "#6b9cff"
    },
    sceneManifest: {
      id: "momo-mania-showcase",
      preloadPriority: "medium",
      assets: ["cdn://games/momo-mania/poster.webp", "cdn://games/momo-mania/highlight.glb"]
    }
  }
];

export const studioPrinciples = [
  "Build every public-facing surface as an extension of the world, story, and identity behind the project.",
  "Keep culture, narrative, and gameplay thinking aligned so the studio message feels coherent from first impression to deeper exploration.",
  "Balance visual ambition with readable structure so the experience stays memorable, usable, and ready to grow."
];
